import { action, computed, flow, makeObservable, observable } from 'mobx';
import { persist } from 'mobx-persist';
import { defaultCurrenciesFilters, defaultCurrency, defaultCurrencyValidation } from '../constants/currencies';
import { ICurrenciesFilters, ICurrencyValidation } from '../screens/General/LanguageCountryCurrency/types/currencies';
import { SCREENS } from 'src/constants/screens';
import client from 'src/services/client';
import { ICurrency } from 'src/types/currency';
import { LoadingStatus, TBooleanInNumber, TPagination, paginationCast } from 'src/types/main';
import { getMessageFromError } from 'src/utils/formatter';
import { booleanNumberReverse } from 'src/utils/helpers';
import { ICountryValidation } from 'src/view/screens/SettingsScreen/screens/General/LanguageCountryCurrency/types/countries';
import { getAbortController, isCanceledRequest } from '~/helpers';
import logger from '~/services/logger';
import { toastError } from '~/utils/toasts';
import { ToastDuration } from '~/view/containers/Toasts';

class CurrenciesStore {
  constructor() {
    makeObservable(this);
  }

  getCurrenciesAbortController = new AbortController();
  currenciesGetTimeoutId: NodeJS.Timeout | null = setTimeout(() => {
    return;
  }, 0);

  @observable public currenciesLoadingStatus: LoadingStatus = LoadingStatus.Init;
  @observable public currencyLoadingStatus: LoadingStatus = LoadingStatus.Init;
  @observable public currencyCreateLoadingStatus: LoadingStatus = LoadingStatus.Init;
  @observable public currencyUpdateLoadingStatus: LoadingStatus = LoadingStatus.Init;
  @observable public currencyDeleteLoadingStatus: LoadingStatus = LoadingStatus.Init;
  @persist @observable currencies: ICurrency[] = [];
  @persist @observable public pagination: TPagination = { currentPage: 0, lastPage: 0, totalRows: 0, perPage: 0 };
  @persist @observable currency: ICurrency | null = null;
  @observable filters: ICurrenciesFilters = defaultCurrenciesFilters;
  @observable edited: boolean = false;
  @observable originIsDefault: TBooleanInNumber = 0;
  @observable validation: ICurrencyValidation = defaultCurrencyValidation;

  @action setFilters = (filters: Partial<ICurrenciesFilters>) => {
    this.filters = { ...this.filters, ...filters };
    this._getCurrenciesWithDelay();
  };

  @computed get isValid(): boolean {
    return (
      this.validation.currency_name.state &&
      this.validation.currency_code.state &&
      this.validation.currency_code_ar.state &&
      this.validation.currency_symbol.state &&
      this.validation.divided.state
    );
  }

  private _getCurrencies = flow(function* (this: CurrenciesStore, pageNumber: number = 1): any {
    this.getCurrenciesAbortController.abort();
    this.getCurrenciesAbortController = getAbortController();

    try {
      this.currenciesLoadingStatus = LoadingStatus.Loading;

      const params: any = {
        page: pageNumber,
        search: this.filters.search || '',
        per_page: this.filters.perPage || undefined,
        sort_field: this.filters.sortField || undefined,
        sort_order: this.filters.sortDir || undefined,
      };

      const response = yield client.get('v4/currencies', {
        params,
        headers: { 'Accept-Language': 'en,ar' },
        signal: this.getCurrenciesAbortController.signal,
      });

      this.currencies = this.sortCurrenciesList(response.data);
      this.pagination = paginationCast(response);
      this.currenciesLoadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      if (isCanceledRequest(error)) {
        this.currenciesLoadingStatus = LoadingStatus.Success;
        return;
      }

      this.currenciesLoadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'CurrenciesStore _getCurrencies', showModal: true });
    }
  }).bind(this);

  @action getCurrency = flow(function* (this: CurrenciesStore, currencyId: number | string): any {
    try {
      this.currencyLoadingStatus = LoadingStatus.Loading;

      const response = yield client.get(`v4/currencies/${currencyId}`, { headers: { 'Accept-Language': 'en,ar' } });

      this.setCurrency(response);
      this.currencyLoadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      this.currencyLoadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'CurrenciesStore getCurrency', showModal: true });
    }
  }).bind(this);

  @action getCurrencies = () => {
    if (this.currenciesLoadingStatus === LoadingStatus.Init) {
      this._getCurrenciesWithNoDelay();
    } else {
      this._getCurrenciesWithDelay();
    }
  };

  @action private _getCurrenciesWithNoDelay = (pageNumber: number = 1) => {
    if (this.currenciesGetTimeoutId) {
      clearTimeout(this.currenciesGetTimeoutId);
    }
    this._getCurrencies(pageNumber);
  };

  @action private _getCurrenciesWithDelay = (pageNumber: number = 1) => {
    if (this.currenciesGetTimeoutId) {
      clearTimeout(this.currenciesGetTimeoutId);
    }
    this.currenciesGetTimeoutId = setTimeout(() => {
      this._getCurrencies(pageNumber);
    }, 700);
  };

  @action public changePage = (pageNumber: number) => {
    this._getCurrenciesWithNoDelay(pageNumber);
  };

  private sortCurrenciesList(currencies: ICurrency[]) {
    return currencies.sort((first, second) => {
      if (first.default_currency) {
        return -1;
      }
      if (second.default_currency) {
        return 1;
      }
      return 0;
    });
  }

  @action public setCurrency = (currency?: ICurrency) => {
    this.currency = null;
    this.currency = currency ? currency : defaultCurrency;
    this.originIsDefault = this.currency.default_currency;
    this.currencyLoadingStatus = LoadingStatus.Success;
    this.currencyDeleteLoadingStatus = LoadingStatus.Init;
    this.currencyCreateLoadingStatus = LoadingStatus.Init;
    this.currencyUpdateLoadingStatus = LoadingStatus.Init;
    this.validation = defaultCurrencyValidation;
  };

  private clearValidationKey(key: string) {
    if (!Object.keys(this.validation).includes(key)) {
      return;
    }
    this.validation = {
      ...this.validation,
      [key]: {
        state: true,
        errorText: undefined,
      } as Partial<ICountryValidation>,
    };
  }

  @action public changeCurrencyHandler = (value: Partial<ICurrency>) => {
    if (!this.currency) return;
    const key = Object.keys(value)[0];
    this.clearValidationKey(key);
    this.currency = { ...this.currency, ...value };
    this.edited = true;
  };

  @action public deleteCurrency = async (currencyId: number) => {
    try {
      this.currenciesLoadingStatus = LoadingStatus.Refreshing;
      this.currencyDeleteLoadingStatus = LoadingStatus.Loading;

      await client.delete(`v4/currencies/${currencyId}`);

      if (this.pagination.currentPage > 0) {
        this._getCurrencies(this.pagination.currentPage);
      }
    } catch (error: any) {
      this.currencyDeleteLoadingStatus = LoadingStatus.Error;
      this.currenciesLoadingStatus = LoadingStatus.Error;

      toastError({ message: getMessageFromError(error), duration: ToastDuration.LONG });
    }
  };

  @action public setAsDefault = async (currencyId: number) => {
    const currency = this.currencies.find((_currency) => _currency.id === currencyId);
    if (!currency) return;

    try {
      this.currenciesLoadingStatus = LoadingStatus.Refreshing;

      await client.post(`v4/currencies/${currencyId}/set-default`);

      this.currencies = this.currencies.map((_currency) =>
        _currency.id === currencyId ? { ..._currency, default_currency: 1 } : { ..._currency, default_currency: 0 }
      );

      this.currencies = this.sortCurrenciesList(this.currencies);
      this.currenciesLoadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      this.currencies = this.currencies.map((_currency) =>
        _currency.id === currencyId ? { ..._currency, default_currency: booleanNumberReverse(_currency.default_currency) } : _currency
      );
      this.currenciesLoadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'CurrenciesStore setAsDefault', showModal: true });
    }
  };

  @action public createCurrency = async () => {
    if (!this.currency) return;

    try {
      this.currencyCreateLoadingStatus = LoadingStatus.Loading;

      const response = await client.post('v4/currencies', { ...this.currency }, { headers: { 'Accept-Language': 'en,ar' } });

      this.currency.id = response.id;
      this.edited = false;
      this.currencyCreateLoadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      this.currencyCreateLoadingStatus = LoadingStatus.Error;

      if (this.isErrorResponseHandled(error)) return;
      logger.error({ error, place: 'CurrenciesStore createCurrency', showModal: true });
    }
  };

  @action public updateCurrency = async () => {
    if (!this.currency?.id) return;

    try {
      this.currencyLoadingStatus = LoadingStatus.Loading;
      this.currencyUpdateLoadingStatus = LoadingStatus.Loading;

      await client.put(`v4/currencies/${this.currency.id}`, { ...this.currency }, { headers: { 'Accept-Language': 'en,ar' } });

      this.edited = false;
      this.currencyLoadingStatus = LoadingStatus.Success;
      this.currencyUpdateLoadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      this.currencyLoadingStatus = LoadingStatus.Error;
      this.currencyUpdateLoadingStatus = LoadingStatus.Error;

      if (this.isErrorResponseHandled(error)) return;
      logger.error({ error, place: 'CurrenciesStore updateCurrency', showModal: true });
    }
  };

  private isErrorResponseHandled(error: any): boolean {
    const errorsBag = error.response.data;
    let hasApiErrors = false;
    if (errorsBag.currency_name) {
      this.validation.currency_name.errorText = errorsBag.currency_name[0];
      this.validation.currency_name.state = false;
      hasApiErrors = true;
    }
    if (errorsBag.currency_code) {
      this.validation.currency_code.errorText = errorsBag.currency_code[0];
      this.validation.currency_code.state = false;
      hasApiErrors = true;
    }
    if (errorsBag.currency_code_ar) {
      this.validation.currency_code_ar.errorText = errorsBag.currency_code_ar[0];
      this.validation.currency_code_ar.state = false;
      hasApiErrors = true;
    }
    if (errorsBag.currency_symbol) {
      this.validation.currency_symbol.errorText = errorsBag.currency_symbol[0];
      this.validation.currency_symbol.state = false;
      hasApiErrors = true;
    }
    if (errorsBag.divided) {
      this.validation.divided.errorText = errorsBag.divided[0];
      this.validation.divided.state = false;
      hasApiErrors = true;
    }
    return hasApiErrors;
  }

  @action unMount = () => {
    this.currency = null;
    this.edited = false;

    if (!window.location.pathname.includes(SCREENS.SETTINGS_CURRENCY())) this.filters = defaultCurrenciesFilters;
  };
}

const currenciesStore = new CurrenciesStore();
export default currenciesStore;
